import {
  useEffect,
  useRef,
  MutableRefObject,
} from 'react';
import { useRouter } from 'next/router';
import ImageGallery, { ReactImageGalleryItem } from 'react-image-gallery';

import {
  ProductContentFullFragment,
  MediaContentFragment,
  VariationContentFragment,
} from '@axis/graphql';
import { useIsMobile, useIsTabletOrMobile } from '@axis/hooks/mobile';

import { ProductGallery } from '@axis/components/ProductGallery';
import { BasketOptions } from '@axis/components/BasketOptions';
import { useAnimation } from '@axis/components/AnimationProvider';

export interface ProductModalProps {
  product: ProductContentFullFragment;
}

export function ProductModal({ product }: ProductModalProps) {
  const galleryRef = useRef<ImageGallery>();

  const isTabletOrMobile = useIsTabletOrMobile();
  const { push } = useRouter();
  const { anime } = useAnimation();

  useEffect(() => {
    anime(
      {
        targets: '.product-modal',
        translateY: isTabletOrMobile ? [300, 0] : [300, '-50%'],
        translateX: isTabletOrMobile ? [0, 0] : ['-50%', '-50%'],
        opacity: [0, 1],
        duration: 300,
        delay: 100,
        easing: 'easeInOutSine',
      },
    );
    anime(
      {
        targets: '.product-modal-overlay',
        opacity: [0, 0.8],
        duration: 500,
        easing: 'easeInOutSine',
      },
    );
  }, [isTabletOrMobile]);

  const backToShop = () => {
    anime(
      {
        targets: '.product-modal',
        translateY: isTabletOrMobile ? [0, 300] : ['-50%', 300],
        translateX: isTabletOrMobile ? [0, 0] : ['-50%', '-50%'],
        opacity: [1, 0],
        duration: 300,
        easing: 'easeInOutSine',
        complete: () => push('/shop', undefined, { shallow: true }),
      },
    );
    anime(
      {
        targets: '.product-modal-overlay',
        opacity: [0.8, 0],
        duration: 300,
        easing: 'easeInOutSine',
      },
    );
  };

  const handleVariationChange = (variation: VariationContentFragment) => {
    if (galleryRef.current) {
      const gallery: ReactImageGalleryItem[] = [...galleryRef.current.props.items];
      const nextIndex = gallery.findIndex(
        ({ original }) => original === variation.image?.sourceUrl,
      );
      galleryRef.current.slideToIndex(nextIndex);
    }
  };

  return (
    <>
      {!isTabletOrMobile && (
        <div
          className="product-modal-overlay drawer-overlay top-20 z-40"
          onClick={backToShop}
          onKeyPress={backToShop}
          role="button"
          tabIndex={-1}
          aria-label="overlay"
        />
      )}
      <div
        className="product-modal drawer
        inset-0
      w-full
      z-40
      top-20
      h-full-20
      md:rounded-xl
      md:top-1/2
      md:left-1/2
      md:-translate-x-1/2
      md:-translate-y-1/2
      md:w-11/12
      lg:w-4/5
      md:h-4/5
      "
      >
        <div className="flex flex-wrap content-start min-h-full md:h-auto gap-5 px-10 pt-10 relative">
          <button
            type="button"
            className="drawer-close-button z-40"
            onClick={backToShop}
          >
            <i className="fa-solid fa-xmark" />
          </button>
          <div className="shop-page-section
          w-full
          md:w-1/2"
          >
            <span className="shop-page-section-header">Gallery</span>
            <ProductGallery
              ref={galleryRef as MutableRefObject<ImageGallery>}
              className="w-full"
              main={product.image as MediaContentFragment}
              images={(product?.galleryImages?.nodes || []) as MediaContentFragment[]}
            />
          </div>
          <div className="shop-page-section
          w-full
          flex-1
          shrink"
          >
            <div className="h-full w-full text-center flex flex-col order-5 md:order-2 md:text-left">
              <div className="font-serif font-bold text-2xl mb-0">{product?.name as string}</div>
              <div className="product-modal-description font-sans text-lg" dangerouslySetInnerHTML={{ __html: product?.shortDescription as string }} />
              {!isTabletOrMobile && (
                <BasketOptions product={product} onVariationChange={handleVariationChange} />
              )}
            </div>
          </div>
          <div className="shop-page-section w-full text-center order-7 md:order-6 md:text-left mb-10">
            <span className="shop-page-section-header">Description</span>
            <div className="product-modal-description font-sans text-base" dangerouslySetInnerHTML={{ __html: product?.description as string }} />
          </div>
        </div>
        {isTabletOrMobile && (
          <BasketOptions product={product} onVariationChange={handleVariationChange} sticky />
        )}
      </div>
    </>
  );
}
